import { cn } from '@/lib/cn';
import { TableMenu } from '@heidi/editor/ui';
import { EditorContent, type Editor } from '@tiptap/react';
import { forwardRef, memo } from 'react';
// Base ProseMirror styling (typography, tables, lists) is shared; markdown-native
// layers its own heading styles (H4/H5 scale, compact section heading) on top.
import styles from '../shared/styles/index.module.scss';
import { MarkdownNativeLinkMenu } from './link-menu';
import { STREAMING_PROSEMIRROR_MUTED_CLASS } from './streaming-prosemirror-muted';
import headingStyles from './styles/markdown-native.module.scss';
import { MarkdownNativeTextMenu } from './text-menu';

export const MarkdownNativeEditor = memo(
  forwardRef<
    HTMLDivElement,
    {
      editor: Editor;
      className?: string;
      footerComponent?: React.ReactNode;
      /**
       * Stop the editor content from growing to fill the scroller, so
       * `footerComponent` sits directly under the last line instead of at the
       * scroller's bottom edge. The note canvas needs this: its scroller ends
       * where the Ask Heidi composer begins, so a pinned footer would move
       * whenever the composer grows or shrinks.
       */
      footerFollowsContent?: boolean;
      /**
       * Container test id. Overridden by hosts whose surface already has an
       * established tag the e2e page objects locate by (the template editor's
       * `<tab>-block-editor`), so a kit swap doesn't move the selector.
       */
      testId?: string;
      /** Note-scoped text-menu affordances; the template surface hides both. */
      enableFindReplace?: boolean;
      enableSnippet?: boolean;
      streamContentMuted?: boolean;
    }
  >(
    (
      {
        editor,
        className,
        footerComponent,
        footerFollowsContent = false,
        testId = 'markdown-native-editor',
        enableFindReplace = true,
        enableSnippet = true,
        streamContentMuted = false,
      },
      ref
    ) => (
      <div
        ref={ref}
        data-testid={testId}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={cn(
          'prose-mirror-container scrollbar-fade relative flex size-full flex-col overflow-y-auto',
          streamContentMuted && STREAMING_PROSEMIRROR_MUTED_CLASS,
          className
        )}
      >
        <EditorContent
          spellCheck={true}
          data-testid="markdown-native-editor-content"
          className={cn(
            styles.ProseMirror,
            headingStyles.ProseMirror,
            footerFollowsContent ? 'flex flex-col' : 'flex flex-1 flex-col'
          )}
          editor={editor}
        />
        <MarkdownNativeTextMenu
          editor={editor}
          enableFindReplace={enableFindReplace}
          enableSnippet={enableSnippet}
        />
        <MarkdownNativeLinkMenu editor={editor} />
        <TableMenu editor={editor} />
        {footerComponent}
      </div>
    )
  )
);

MarkdownNativeEditor.displayName = 'MarkdownNativeEditor';
